import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import OrganizationList from "./OrganizationList";
import OrganizationAddress from "./OrganizationAddress";
import OrganizationContact from "./OrganizationContact";
import OrganizationImages from "./OrganizationImages";
import axiosOrganizationInstance from "./AxiosOrganizationInstance";
import {
  getStoredOrganizationId,
  getStoredUserId,
  setStoredOrganizationId,
} from "./organizationStorage";

import organizationLogo from './assets/business-os-logo.png';
import './css/dashboard.css';

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [organizationId, setOrganizationId] = useState(null);
  const [status, setStatus] = useState("");

  // ✅ Get tab from URL safely
  const getTabFromURL = () => {
    const params = new URLSearchParams(location.search);
    return params.get("tab") || "Details";
  };

  const [activeTab, setActiveTab] = useState(getTabFromURL);

  // Fetch organizationId and status for Images tab
  useEffect(() => {
    const loadOrganizationContext = async () => {
      try {
        const token = localStorage.getItem("Token");
        let orgId = getStoredOrganizationId();
        const userId = getStoredUserId();

        if (!orgId && userId) {
          const linkResponse = await axiosOrganizationInstance.get(
            `/organization/user/${userId}/organization`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          orgId = String(linkResponse.data.organizationId);
          setStoredOrganizationId(orgId);
        }

        if (!orgId) {
          setOrganizationId(null);
          return;
        }

        setOrganizationId(orgId);

        const response = await axiosOrganizationInstance.get(`/organization/${orgId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setStatus(response.data?.status || "");
      } catch (error) {
        console.error("Error fetching organization status:", error);
      }
    };

    loadOrganizationContext();
  }, []);

  // ✅ Ensure state updates when URL changes
  useEffect(() => {
    const newTab = getTabFromURL();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.search]);

  // ✅ Ensure URL updates only when tab changes
  useEffect(() => {
    if (activeTab && location.search !== `?tab=${activeTab}`) {
      navigate(`?tab=${activeTab}`, { replace: true });
    }
  }, [activeTab, navigate, location.search]);

  const renderComponent = () => {
    switch (activeTab) {
      case "Details":
        return <OrganizationList />;
      case "Address":
        return <OrganizationAddress />;
      case "Contacts":
        return <OrganizationContact />;
      case "Images":
        return <OrganizationImages organizationId={organizationId} status={status} editMode={false} />;
      default:
        return <div>Error: Tab not found</div>;
    }
  };

  return (
    <div className="organization-dashboard">
      {/* Sidebar */}
      <div className="organization-dashboard-sidebar">
        <img src={organizationLogo} alt='Dashboard Logo' width='100' height='100' />
        <div style={{ display: "flex", flexDirection: "column", marginTop: "20px", gap: "10px" }}>
          {["Details", "Address", "Contacts", "Images"].map((tab) => (
            <p
              key={tab}
              style={{
                fontSize: '14px',
                fontWeight: '600',
                lineHeight: '20px',
                color: activeTab === tab ? '#ffffff' : '#000000',
                backgroundColor: activeTab === tab ? "#445164" : "transparent",
                padding: "8px 12px",
                borderRadius: "5px",
                cursor: "pointer"
              }}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </p>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content organization-dashboard-main">
        {renderComponent()}
      </div>
    </div>
  );
};

export default Dashboard;
